<template>
    <div class="card">
        <div class="bg-dark card-header text-white">Academic Indirect Cost Distribution - By PI</div>
        <div class="card-body">
            <div v-if="!isLoading" class="table-responsive">
                <table class="table table-sm">
                    <thead>
                        <tr>
                            <th style="width: 150px !important;"></th>
                            <th>Name</th>
                            <th style="width: 100px !important;">UIN</th>
                            <th style="width: 100px !important;">PI Account (GL)</th>
                            <th style="width: 200px !important;">PI Account (SL-SA)</th>
                            <th style="width: 150px !important;text-align: end;">Total PI IDC Credit</th>
                            <th style="width: 150px !important;text-align: end;">Total RDF Credit</th>
                        </tr>
                  </thead>
                  <tbody>
                      <template v-for="(responsiblePerson, index) in responsiblePeople">
                        <tr :key="index">
                          <td>
                            <div class="row">
                              <div class="col-4 col-lg-3">
                                <a @click="toggleAccounts(index)" class="ml-3">
                                  <i v-if="!responsiblePerson.showAccounts" class="fa fa-caret-down fa-lg" />
                                  <i v-if="responsiblePerson.showAccounts" class="fa fa-caret-up fa-lg" />
                                </a>
                              </div>
                              <div class="btn-group col-auto ml-2">
                                  <button v-if="canEdit" @click="onEditResponsiblePerson(responsiblePerson)" class="btn btn-warning btn-sm d-print-none" title="Edit Responsible Person"
                                          type="button"><i class="fa fa-pencil-alt"></i>
                                  </button>
                              </div>
                            </div>
                          </td>
                          <td>{{ responsiblePerson.responsiblePersonName }}</td>
                          <td>{{ responsiblePerson.responsiblePersonUIN }}</td>
                          <td>{{ responsiblePerson.pigl }}</td>
                          <td>{{ responsiblePerson.pisl }} - {{ responsiblePerson.pisa }}</td>
                          <td style="text-align: end;">{{ responsiblePerson.piidcCredit | currency }}</td>
                          <td style="text-align: end;">{{ responsiblePerson.rdfCredit | currency }}</td>
                        </tr>
                        <tr v-if="responsiblePerson.showAccounts" :key="index">
                          <td colspan="4"></td>
                          <td colspan="3">
                            <table class="table table-sm accounts-table">
                              <thead>
                                <tr>
                                  <th style="width: 200px !important;">Account (SL-SA)</th>
                                  <th style="width: 150px !important;text-align:end;">PI IDC Credit</th>
                                  <th style="width: 150px !important;text-align:end;">RDF Credit</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr v-for="account in responsiblePerson.adLocAccounts" :key="account">
                                  <td>{{ account.sl }} - {{ account.sa }}</td>
                                  <td style="text-align:end;">{{ account.piidcCredit | currency }}</td>
                                  <td style="text-align:end;">{{ account.rdfCredit | currency }}</td>
                                </tr>
                                <tr>
                                  <td style="font-weight: bold;text-align:end;">Total</td>
                                  <td style="font-weight: bold;border-top: 3px double;border-bottom: 3px double;text-align:end;">{{ responsiblePerson.piidcCredit | currency }}</td>
                                  <td style="font-weight: bold;border-top: 3px double;border-bottom: 3px double;text-align:end;">{{ responsiblePerson.rdfCredit | currency }}</td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      </template>
                      <tr>
                        <td colspan="5" style="font-weight: bold;text-align:end;">Total</td>
                        <td style="font-weight: bold;border-top: 3px double;border-bottom: 3px double;text-align:end;">{{ totalPIIDCCredit | currency }}</td>
                        <td style="font-weight: bold;border-top: 3px double;border-bottom: 3px double;text-align:end;">{{ totalRDFCredit | currency }}</td>
                      </tr>
                  </tbody>
                </table>
            </div>
        </div>
        <AppModal @close="onEditResponsiblePersonClose()" v-if="isEditModalVisible">
            <template v-slot:header>Edit PI</template>
            <form @submit.prevent="onEditResponsiblePersonSubmit()">
                <transition name="fade">
                    <AppNotification :type="alertMessage.type" v-if="alertMessage.message">
                        {{alertMessage.message}}
                    </AppNotification>
                </transition>
                <div class="form-group">
                    <label for="newResponsiblePersonName">Name</label>
                    <input :class="{'is-invalid': $v.newResponsiblePersonName.$invalid}" class="form-control" v-model="newResponsiblePersonName" />
                    <span class="invalid-feedback" v-if="!$v.newResponsiblePersonName.required">Field is required</span>
                </div>
                <div class="form-group">
                    <label for="newResponsiblePersonUIN">UIN</label>
                    <input :class="{'is-invalid': $v.newResponsiblePersonUIN.$invalid}" class="form-control" v-model="newResponsiblePersonUIN" />
                    <span class="invalid-feedback" v-if="!$v.newResponsiblePersonUIN.required">Field is required</span>
                </div>
                <div class="form-group">
                    <label for="newPIGL">PI Account GL</label>
                    <input :class="{'is-invalid': $v.newPIGL.$invalid}" class="form-control" v-model="newPIGL" />
                    <span class="invalid-feedback" v-if="!$v.newPIGL.required">Field is required</span>
                </div>
                <div class="form-group">
                    <label for="newPISL">PI Account SL</label>
                    <input :class="{'is-invalid': $v.newPISL.$invalid}" class="form-control" v-model="newPISL" />
                    <span class="invalid-feedback" v-if="!$v.newPISL.required">Field is required</span>
                </div>
                <div class="form-group">
                    <label for="newPISA">PI Account SA</label>
                    <input :class="{'is-invalid': $v.newPISA.$invalid}" class="form-control" v-model="newPISA" />
                    <span class="invalid-feedback" v-if="!$v.newPISA.required">Field is required</span>
                </div>
            </form>
            <template v-slot:footer>
                <button :disabled="isSubmitting" @click.prevent="onEditResponsiblePersonSubmit()" class="btn btn-primary" type="submit">Save
                </button>
                <button :disabled="isSubmitting" @click="onEditResponsiblePersonClose()" class="btn btn-danger" data-dismiss="modal"
                        type="button">
                    Close
                </button>
            </template>
        </AppModal>
    </div>
</template>

<script>
    import AcademicIndirectCostService from "../../services/AcademicIndirectCostService";
    import { currency } from "../../filters/Currency";
    import AppModal from "@/components/shared/AppModal.vue"
    import {ToastType} from "@/services/models/shared/ToastType";
    import {required} from "vuelidate/lib/validators";

    export default {
        components: {
            AppModal
        },
        props: {
          adLoc: {
            required: true,
            type: String
          },
          fiscalYear: {
            required: true,
            type: Number
          },
          payPeriodStartDate: {
            required: true,
            type: Date
          },
          canEdit: {
            required: true,
            type: Boolean
          },
          departmentName: {
            required: true,
            type: String
          }
        },
        filters: {
            currency
        },
        data() {
            return {
                responsiblePeople: [],
                isLoading: false,
                filterOptions: {
                  payPeriodStartDate: null,
                  adLoc: null
                },
                newResponsiblePersonName: null,
                newResponsiblePersonUIN: null,
                newPIGL: null,
                newPISL: null,
                newPISA: null,
                previousResponsiblePersonName: null,
                previousResponsiblePersonUIN: null,
                previousPIGL: null,
                previousPISL: null,
                previousPISA: null,
                isEditModalVisible: false,
                alertMessage: {
                    type: null,
                    message: null
                },
                isSubmitting: false
            }
        },
        computed: {
          totalPIIDCCredit() {
            return this.responsiblePeople.map((obj) => { return obj.piidcCredit }).reduce((accumulator, currentValue) => accumulator + currentValue, 0);
          },

          totalRDFCredit() {
            return this.responsiblePeople.map((obj) => { return obj.rdfCredit }).reduce((accumulator, currentValue) => accumulator + currentValue, 0);
          }
        },
        methods: {
            /**
             * @description Loads the data from the API
             */
            loadData() {
                const vm = this;
                vm.isLoading = true;

                AcademicIndirectCostService.getResponsiblePeople(vm.filterOptions)
                    .then(response => {
                        vm.responsiblePeople = response.map((obj) => { return {...obj, showAccounts: false }});
                    })
                    .catch(() => {
                        vm.redirectToError()
                    })
                    .finally(() => {
                        vm.isLoading = false;
                    })
            },
            
            toggleAccounts (index) {
              this.responsiblePeople[index].showAccounts = !this.responsiblePeople[index].showAccounts;
            },

            onEditResponsiblePerson (responsiblePerson) {
              this.newResponsiblePersonName = responsiblePerson.responsiblePersonName;
              this.newResponsiblePersonUIN = responsiblePerson.responsiblePersonUIN;
              this.newPIGL = responsiblePerson.pigl;
              this.newPISL = responsiblePerson.pisl;
              this.newPISA = responsiblePerson.pisa;
              this.previousResponsiblePersonName = responsiblePerson.responsiblePersonName;
              this.previousResponsiblePersonUIN = responsiblePerson.responsiblePersonUIN;
              this.previousPIGL = responsiblePerson.pigl;
              this.previousPISL = responsiblePerson.pisl;
              this.previousPISA = responsiblePerson.pisa;
              this.isEditModalVisible = true;
            },

            onEditResponsiblePersonClose () {
              this.newResponsiblePersonName = null;
              this.newResponsiblePersonUIN = null;
              this.newPIGL = null;
              this.newPISL = null;
              this.newPISA = null;
              this.previousResponsiblePersonName = null;
              this.previousResponsiblePersonUIN = null;
              this.previousPIGL = null;
              this.previousPISL = null;
              this.previousPISA = null;
              this.isEditModalVisible = false;
            },

            onEditResponsiblePersonSubmit() {
                const vm = this;

                // stop here if form is invalid
                this.$v.$touch();
                if (this.$v.$invalid) {
                    return;
                }

                vm.isSubmitting = true;
                AcademicIndirectCostService.updateResponsiblePerson({
                    payPeriodStartDate: this.payPeriodStartDate,
                    adLoc: this.adLoc,
                    previousResponsiblePersonName: this.previousResponsiblePersonName,
                    previousResponsiblePersonUIN: this.previousResponsiblePersonUIN,
                    previousPIGL: this.previousPIGL,
                    previousPISL: this.previousPISL,
                    previousPISA: this.previousPISA,
                    newResponsiblePersonName: this.newResponsiblePersonName,
                    newResponsiblePersonUIN: this.newResponsiblePersonUIN,
                    newPIGL: this.newPIGL,
                    newPISL: this.newPISL,
                    newPISA: this.newPISA
                }).then(() => {
                    vm.alert(ToastType.Success, `PI information was updated successfully`);
                    vm.onEditResponsiblePersonClose();
                    vm.loadData();
                }).catch(function (error) {
                    vm.alertMessage.type = "danger";
                    vm.alertMessage.message = error.response.statusText;
                }).finally(() => {
                    vm.isSubmitting = false;
                });
            }
        },
        created() {
            this.filterOptions.payPeriodStartDate = this.payPeriodStartDate;
            this.filterOptions.adLoc = this.adLoc;
            this.loadData();
        },
        validations: {
            newResponsiblePersonName: {
              required
            },
            newResponsiblePersonUIN: {
              required
            },
            newPIGL: {
              required
            },
            newPISL: {
              required
            },
            newPISA: {
              required
            }
        }
    }
</script>
  
<style scoped>
  table.accounts-table {
    background-color: rgba(0,0,0,.05) !important;
  }
</style>